import React, { useState, useEffect } from 'react';
import { useModal } from '../context/ModalDialogContext';
import { DATE_TIME_FORMAT_PRESETS, formatDateTimeValue } from '../utils/dateTimeFormatter';
import SpeciesLibrary from './SpeciesLibrary';

export default function DataManager({ user }) {
  const modal = useModal();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  const isGlobalRole = user?.role === 'GLOBAL_ADMIN' || user?.role === 'GLOBAL_POWER' || user?.role === 'GLOBAL_STANDARD';
  const isGlobalAdmin = user?.role === 'GLOBAL_ADMIN' || user?.role === 'GLOBAL_POWER';
  const [activeSubTab, setActiveSubTab] = useState(isGlobalRole ? 'datasets' : 'species'); // 'datasets' | 'species'

  // States
  const [dataGroups, setDataGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [sharedTenantIds, setSharedTenantIds] = useState([]);
  
  // Add Group / Edit Group Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // Full group object if editing details
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');

  // Options List Modal States
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsList, setOptionsList] = useState([]);
  const [newOptionInput, setNewOptionInput] = useState('');

  // Regex Renderers Modal States
  const [showRegexModal, setShowRegexModal] = useState(false);
  const [customRenderers, setCustomRenderers] = useState(() => {
    try {
      const saved = localStorage.getItem('customRegexRenderers');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Failed to parse customRegexRenderers:", e);
      return [];
    }
  });
  const [editingRendererId, setEditingRendererId] = useState(null);
  const [regexBuilderName, setRegexBuilderName] = useState('');
  const [regexBuilderPattern, setRegexBuilderPattern] = useState('');
  const [regexBuilderReplace, setRegexBuilderReplace] = useState('');
  const [regexBuilderTestInput, setRegexBuilderTestInput] = useState('');

  // Data Sets Sync Modal States
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncTab, setSyncTab] = useState('direct'); // 'direct' | 'file'
  const [syncTargetUrl, setSyncTargetUrl] = useState(
    isDev ? 'https://trophycoin-app-982827923504.us-central1.run.app' : 'http://localhost:8000'
  );
  const [syncAdminEmail, setSyncAdminEmail] = useState(user?.email || 'admin@trophycoin.io');
  const [syncAdminPassword, setSyncAdminPassword] = useState('');
  const [syncDirection, setSyncDirection] = useState('push'); // 'push' | 'pull'
  const [syncMode, setSyncMode] = useState('merge'); // 'merge' | 'add_only'
  const [syncIncludeCustomRenderers, setSyncIncludeCustomRenderers] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncStatusMsg, setSyncStatusMsg] = useState('');
  const [syncModalError, setSyncModalError] = useState('');
  const [syncModalSuccess, setSyncModalSuccess] = useState('');
  
  // File Import States
  const [importJsonData, setImportJsonData] = useState(null);
  const [importJsonFileName, setImportJsonFileName] = useState('');

  // Share Modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareGroupId, setShareGroupId] = useState(null);

  // Field Editor States
  const [fields, setFields] = useState([]);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [draggedFieldIndex, setDraggedFieldIndex] = useState(null);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldAlias, setNewFieldAlias] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldSample, setNewFieldSample] = useState('');
  const [newFieldRender, setNewFieldRender] = useState('none');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  
  // Field Type Validation Configs
  const [numIsFloat, setNumIsFloat] = useState(false);
  const [numMin, setNumMin] = useState('');
  const [numMax, setNumMax] = useState('');
  const [numMinLength, setNumMinLength] = useState('');
  const [numMaxLength, setNumMaxLength] = useState('');

  const [textValidator, setTextValidator] = useState('none'); // 'none', 'email', 'website'
  const [textMinLength, setTextMinLength] = useState('');
  const [textMaxLength, setTextMaxLength] = useState('');

  const [coordFormat, setCoordFormat] = useState('utm'); // 'utm', 'dd', 'dms'
  const [dateTimeFormat, setDateTimeFormat] = useState('');

  // Dynamic Options (Species Library) States
  const [optionSource, setOptionSource] = useState('static'); // 'static' | 'species_library'
  const [speciesField, setSpeciesField] = useState('common_name');
  const [speciesCategory, setSpeciesCategory] = useState('All');
  const [speciesLibraryPreview, setSpeciesLibraryPreview] = useState([]);

  const fetchSpeciesPreview = async (cat = 'All') => {
    try {
      const url = cat && cat !== 'All'
        ? `${API_BASE}/api/v1/species?category=${encodeURIComponent(cat)}`
        : `${API_BASE}/api/v1/species`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSpeciesLibraryPreview(data);
      }
    } catch (e) {
      console.warn("Could not fetch species preview:", e);
    }
  };


  // Status Alerts
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Fetch all groups and tenants
  const fetchData = async () => {
    try {
      const resGroups = await fetch(`${API_BASE}/api/v1/data-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resGroups.ok) {
        const data = await resGroups.json();
        setDataGroups(data);
      }

      const resTenants = await fetch(`${API_BASE}/api/v1/users/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resTenants.ok) {
        const data = await resTenants.json();
        setTenants(data);
      }
    } catch (err) {
      console.error("Error fetching data manager resources:", err);
      setErrorMsg("Failed to connect to backend service.");
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Flash alert helper
  const triggerSuccess = (msg) => {
    setSuccessMsg(msg);
    if (modal?.notify) modal.notify(msg, 'success');
    setTimeout(() => setSuccessMsg(''), 4000);
  };
  const triggerError = (msg) => {
    setErrorMsg(msg);
    if (modal?.notify) modal.notify(msg, 'error');
    setTimeout(() => setErrorMsg(''), 4000);
  };

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (successMsg) modal.notify(successMsg, 'success');
  }, [successMsg, modal]);

  useEffect(() => {
    if (errorMsg) modal.notify(errorMsg, 'error');
  }, [errorMsg, modal]);

  useEffect(() => {
    if (syncModalSuccess) modal.notify(syncModalSuccess, 'success');
  }, [syncModalSuccess, modal]);

  // ----------------------------------------------------
  // DATA SETS EXPORT & SYNC HANDLERS
  // ----------------------------------------------------
  const handleQuickExportJson = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to export data sets.');
      }
      const data = await res.json();
      if (customRenderers && customRenderers.length > 0) {
        data.custom_renderers = customRenderers;
      }
      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `trophycoin_datasets_${dateStr}.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      triggerSuccess(`Exported ${data.count || data.data_groups?.length || 0} data sets successfully!`);
    } catch (err) {
      console.error('Export error:', err);
      triggerError(err.message || 'Export failed.');
    }
  };

  const handleFileImportChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportJsonFileName(file.name);
    setSyncModalError('');
    setSyncModalSuccess('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        if (!parsed) throw new Error('Empty JSON file');
        const groups = Array.isArray(parsed) ? parsed : parsed.data_groups;
        if (!groups || !Array.isArray(groups)) {
          throw new Error("File must contain a 'data_groups' array or be a JSON array of data sets.");
        }
        setImportJsonData(parsed);
      } catch (err) {
        setSyncModalError(`Failed to parse JSON file: ${err.message}`);
        setImportJsonData(null);
      }
    };
    reader.onerror = () => {
      setSyncModalError('Failed to read file.');
      setImportJsonData(null);
    };
    reader.readAsText(file);
  };

  const handleExecuteFileImport = async () => {
    if (!importJsonData) {
      setSyncModalError('Please select a valid datasets JSON file first.');
      return;
    }

    setSyncLoading(true);
    setSyncModalError('');
    setSyncModalSuccess('');
    try {
      const groups = Array.isArray(importJsonData)
        ? importJsonData
        : (importJsonData.data_groups || []);

      const payload = {
        schema_version: 'trophycoin_datasets_v1',
        sync_mode: syncMode,
        data_groups: groups,
        custom_renderers: importJsonData.custom_renderers || []
      };

      const res = await fetch(`${API_BASE}/api/v1/data-groups/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'File import failed.');
      }

      const result = await res.json();
      if (syncIncludeCustomRenderers && importJsonData.custom_renderers && Array.isArray(importJsonData.custom_renderers)) {
        const existingIds = new Set(customRenderers.map(r => r.id));
        const toAdd = importJsonData.custom_renderers.filter(r => !existingIds.has(r.id));
        if (toAdd.length > 0) {
          const merged = [...customRenderers, ...toAdd];
          setCustomRenderers(merged);
          localStorage.setItem('customRegexRenderers', JSON.stringify(merged));
        }
      }

      await fetchData();
      setSyncModalSuccess(
        `✅ Imported successfully: ${result.created_count} created, ${result.updated_count} updated!`
      );
      triggerSuccess('Data sets imported successfully!');
    } catch (err) {
      console.error('File import error:', err);
      setSyncModalError(err.message || 'Import failed.');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleExecuteRemoteSync = async () => {
    if (!syncTargetUrl.trim()) {
      setSyncModalError('Please enter the target server URL.');
      return;
    }
    if (!syncAdminEmail.trim() || !syncAdminPassword) {
      setSyncModalError('Please enter admin email and password for the target server.');
      return;
    }

    setSyncLoading(true);
    setSyncModalError('');
    setSyncModalSuccess('');
    setSyncStatusMsg('Authenticating with target server...');

    try {
      const targetBase = syncTargetUrl.trim().replace(/\/+$/, '');

      // 1. Authenticate with target server
      const loginRes = await fetch(`${targetBase}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: syncAdminEmail.trim(),
          password: syncAdminPassword
        })
      });

      if (!loginRes.ok) {
        const err = await loginRes.json().catch(() => ({}));
        throw new Error(`Authentication at ${targetBase} failed: ${err.detail || 'Invalid credentials'}`);
      }
      const loginData = await loginRes.json();
      const remoteToken = loginData.access_token;
      if (!remoteToken) {
        throw new Error('Did not receive access token from target server.');
      }

      if (syncDirection === 'push') {
        // PUSH LOCAL -> REMOTE
        setSyncStatusMsg('Exporting local data sets...');
        const localExportRes = await fetch(`${API_BASE}/api/v1/data-groups/export`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!localExportRes.ok) {
          const err = await localExportRes.json().catch(() => ({}));
          throw new Error(`Failed to read local datasets: ${err.detail || 'Export error'}`);
        }
        const exportData = await localExportRes.json();

        setSyncStatusMsg(`Transferring ${exportData.count || exportData.data_groups?.length || 0} data sets to ${targetBase}...`);
        const importPayload = {
          schema_version: 'trophycoin_datasets_v1',
          sync_mode: syncMode,
          data_groups: exportData.data_groups || [],
          custom_renderers: syncIncludeCustomRenderers ? customRenderers : []
        };

        const pushRes = await fetch(`${targetBase}/api/v1/data-groups/import`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${remoteToken}`
          },
          body: JSON.stringify(importPayload)
        });

        if (!pushRes.ok) {
          const err = await pushRes.json().catch(() => ({}));
          throw new Error(`Target server import error: ${err.detail || 'Push failed'}`);
        }

        const pushResult = await pushRes.json();
        setSyncModalSuccess(
          `✅ Successfully pushed to ${targetBase}! (${pushResult.created_count} created, ${pushResult.updated_count} updated)`
        );
        triggerSuccess(`Pushed data sets to ${targetBase} successfully!`);
      } else {
        // PULL REMOTE -> LOCAL
        setSyncStatusMsg(`Fetching data sets from ${targetBase}...`);
        const remoteExportRes = await fetch(`${targetBase}/api/v1/data-groups/export`, {
          headers: { Authorization: `Bearer ${remoteToken}` }
        });
        if (!remoteExportRes.ok) {
          const err = await remoteExportRes.json().catch(() => ({}));
          throw new Error(`Failed to fetch remote datasets: ${err.detail || 'Export error'}`);
        }
        const remoteData = await remoteExportRes.json();

        setSyncStatusMsg(`Applying ${remoteData.count || remoteData.data_groups?.length || 0} data sets locally...`);
        const importPayload = {
          schema_version: 'trophycoin_datasets_v1',
          sync_mode: syncMode,
          data_groups: remoteData.data_groups || [],
          custom_renderers: remoteData.custom_renderers || []
        };

        const localImportRes = await fetch(`${API_BASE}/api/v1/data-groups/import`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(importPayload)
        });

        if (!localImportRes.ok) {
          const err = await localImportRes.json().catch(() => ({}));
          throw new Error(`Local import failed: ${err.detail || 'Import error'}`);
        }

        const importResult = await localImportRes.json();
        if (syncIncludeCustomRenderers && remoteData.custom_renderers && Array.isArray(remoteData.custom_renderers)) {
          const existingIds = new Set(customRenderers.map(r => r.id));
          const toAdd = remoteData.custom_renderers.filter(r => !existingIds.has(r.id));
          if (toAdd.length > 0) {
            const merged = [...customRenderers, ...toAdd];
            setCustomRenderers(merged);
            localStorage.setItem('customRegexRenderers', JSON.stringify(merged));
          }
        }

        await fetchData();
        setSyncModalSuccess(
          `✅ Successfully pulled from ${targetBase}! (${importResult.created_count} created, ${importResult.updated_count} updated)`
        );
        triggerSuccess(`Pulled data sets from ${targetBase} successfully!`);
      }
    } catch (err) {
      console.error('Remote sync error:', err);
      setSyncModalError(err.message || 'Remote synchronization failed.');
    } finally {
      setSyncLoading(false);
      setSyncStatusMsg('');
    }
  };

  // Add Data Group First Stage (Name / Desc)
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newGroupName,
          description: newGroupDesc,
          fields: []
        })
      });

      if (res.ok) {
        const created = await res.json();
        setDataGroups([...dataGroups, created]);
        setSelectedGroup(created);
        setFields([]);
        setEditingFieldIndex(null);
        setNewGroupName('');
        setNewGroupDesc('');
        setShowAddModal(false);
        triggerSuccess("Data Group created successfully! Now defining fields.");
      } else {
        const err = await res.json();
        triggerError(err.detail || "Failed to create data group.");
      }
    } catch (err) {
      triggerError("Network error while creating group.");
    }
  };

  // Open Edit details Modal
  const handleOpenEditDetails = (group) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setNewGroupDesc(group.description || '');
    setShowAddModal(true);
  };

  // Save Data Group Details (Name / Desc) update to backend
  const handleUpdateGroupDetails = async (e) => {
    e.preventDefault();
    if (!editingGroup || !newGroupName.trim()) return;

    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups/${editingGroup.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newGroupName,
          description: newGroupDesc
        })
      });

      if (res.ok) {
        const updated = await res.json();
        setDataGroups(dataGroups.map(g => g.id === updated.id ? updated : g));
        if (selectedGroup?.id === updated.id) {
          setSelectedGroup(updated);
        }
        setEditingGroup(null);
        setNewGroupName('');
        setNewGroupDesc('');
        setShowAddModal(false);
        triggerSuccess("Data Group details updated successfully.");
      } else {
        const err = await res.json();
        triggerError(err.detail || "Failed to update details.");
      }
    } catch (err) {
      triggerError("Network error during details update.");
    }
  };

  // Duplicate dataset schema
  const handleDuplicateGroup = async (group) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: `Copy of ${group.name}`,
          description: group.description,
          fields: group.fields || []
        })
      });

      if (res.ok) {
        const duplicated = await res.json();
        setDataGroups([...dataGroups, duplicated]);
        triggerSuccess(`Duplicated dataset successfully as '${duplicated.name}'.`);
      } else {
        const err = await res.json();
        triggerError(err.detail || "Failed to duplicate data group.");
      }
    } catch (err) {
      triggerError("Network error while duplicating data group.");
    }
  };

  // Open Editor
  const handleOpenEditor = (group) => {
    setSelectedGroup(group);
    setFields(group.fields || []);
    setEditingFieldIndex(null);
    resetFieldForm();
  };



  const handleDragStart = (e, index) => {
    setDraggedFieldIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedFieldIndex === null || draggedFieldIndex === targetIndex) return;

    const updatedFields = [...fields];
    const draggedField = updatedFields[draggedFieldIndex];
    
    // Remove from old position and insert at new position
    updatedFields.splice(draggedFieldIndex, 1);
    updatedFields.splice(targetIndex, 0, draggedField);

    setFields(updatedFields);
    
    // If editing, adjust editingFieldIndex to match new position
    if (editingFieldIndex !== null) {
      if (editingFieldIndex === draggedFieldIndex) {
        setEditingFieldIndex(targetIndex);
      } else if (draggedFieldIndex < editingFieldIndex && targetIndex >= editingFieldIndex) {
        setEditingFieldIndex(editingFieldIndex - 1);
      } else if (draggedFieldIndex > editingFieldIndex && targetIndex <= editingFieldIndex) {
        setEditingFieldIndex(editingFieldIndex + 1);
      }
    }
    
    setDraggedFieldIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedFieldIndex(null);
  };

  const deleteField = (idx) => {
    setFields(fields.filter((_, i) => i !== idx));
    if (editingFieldIndex === idx) {
      setEditingFieldIndex(null);
      resetFieldForm();
    } else if (editingFieldIndex > idx) {
      setEditingFieldIndex(editingFieldIndex - 1);
    }
  };

  const getFormattedSampleData = (sampleData, renderId) => {
    if (!sampleData) return '';
    if (!renderId || renderId === 'none') return sampleData;

    const strVal = String(sampleData).trim();

    if (renderId === 'uppercase') {
      return strVal.toUpperCase();
    }
    if (renderId === 'lowercase') {
      return strVal.toLowerCase();
    }
    if (renderId === 'titlecase') {
      return strVal.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }
    if (renderId === 'metric_to_imperial') {
      const numMatch = strVal.match(/^([\d.]+)\s*(kg|cm|m|g|ml)?$/i);
      if (numMatch) {
        const num = parseFloat(numMatch[1]);
        const unit = numMatch[2] ? numMatch[2].toLowerCase() : '';
        if (unit === 'kg') {
          return `${(num * 2.20462).toFixed(1)} lbs`;
        }
        if (unit === 'g') {
          return `${(num * 0.035274).toFixed(1)} oz`;
        }
        if (unit === 'cm') {
          return `${(num * 0.393701).toFixed(1)} in`;
        }
        if (unit === 'm') {
          return `${(num * 3.28084).toFixed(1)} ft`;
        }
      }
      return strVal;
    }
    if (renderId === 'pad_5') {
      const cleanNum = strVal.replace(/\D/g, '');
      if (!cleanNum) return strVal;
      return cleanNum.padStart(5, '0');
    }
    if (renderId === 'prepend_hash_pad_5') {
      const cleanNum = strVal.replace(/\D/g, '');
      if (!cleanNum) return strVal;
      return `#${cleanNum.padStart(5, '0')}`;
    }

    const rule = customRenderers.find(r => r.id === renderId);
    if (rule && rule.regex) {
      try {
        let pattern = rule.regex;
        let flags = 'g';
        if (pattern.startsWith('/')) {
          const lastSlashIdx = pattern.lastIndexOf('/');
          if (lastSlashIdx > 0) {
            flags = pattern.slice(lastSlashIdx + 1) || 'g';
            pattern = pattern.slice(1, lastSlashIdx);
          }
        }
        const re = new RegExp(pattern, flags);
        return strVal.replace(re, rule.replace || '');
      } catch (e) {
        console.error("Custom regex formatting error:", e);
      }
    }

    return strVal;
  };

  // Edit action: Load field configuration back into form
  const handleEditField = (field, idx) => {
    setEditingFieldIndex(idx);
    setNewFieldLabel(field.label);
    setNewFieldAlias(field.alias || '');
    setNewFieldType(field.type);
    setNewFieldRequired(field.required);
    setNewFieldSample(field.sample_data || '');
    setNewFieldRender(field.render || 'none');
    setNewFieldDescription(field.description || '');

    // Reset validation/options loaders
    setNumIsFloat(false);
    setNumMin('');
    setNumMax('');
    setNumMinLength('');
    setNumMaxLength('');
    setTextValidator('none');
    setTextMinLength('');
    setTextMaxLength('');
    setCoordFormat('utm');
    setDateTimeFormat(field.date_format || field.validation?.date_format || '');


    const v = field.validation || {};
    if (field.type === 'number') {
      setNumIsFloat(!!v.float_decimals);
      setNumMin(v.min !== undefined ? v.min : '');
      setNumMax(v.max !== undefined ? v.max : '');
      setNumMinLength(v.min_length !== undefined ? v.min_length : '');
      setNumMaxLength(v.max_length !== undefined ? v.max_length : '');
    } else if (field.type === 'text') {
      setTextValidator(v.validator_type || 'none');
      setTextMinLength(v.min_length !== undefined ? v.min_length : '');
      setTextMaxLength(v.max_length !== undefined ? v.max_length : '');
    } else if (field.type === 'coordinates') {
      setCoordFormat(v.format || 'utm');
    } else if (field.type === 'options') {
      setOptionsList(field.options || []);
      const src = field.option_source || 'static';
      setOptionSource(src);
      setSpeciesField(field.species_field || 'common_name');
      const cat = field.species_category || 'All';
      setSpeciesCategory(cat);
      if (src === 'species_library') {
        fetchSpeciesPreview(cat);
      }
    }
  };

  // Reset form helper
  const resetFieldForm = () => {
    setNewFieldLabel('');
    setNewFieldAlias('');
    setNewFieldRequired(false);
    setNewFieldSample('');
    setNewFieldType('text');
    setNumIsFloat(false);
    setNumMin('');
    setNumMax('');
    setNumMinLength('');
    setNumMaxLength('');
    setTextValidator('none');
    setTextMinLength('');
    setTextMaxLength('');
    setOptionsList([]);
    setOptionSource('static');
    setSpeciesField('common_name');
    setSpeciesCategory('All');
    setSpeciesLibraryPreview([]);
    setNewOptionInput('');
    setNewFieldRender('none');
    setNewFieldDescription('');
    setDateTimeFormat('');
  };

  // Add or Update Field submit handler
  const handleAddField = (e) => {
    e.preventDefault();
    const labelClean = newFieldLabel.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (!labelClean) {
      triggerError("Please enter a valid field label.");
      return;
    }

    // Uniqueness validation
    if (editingFieldIndex === null) {
      if (fields.some(f => f.label === labelClean)) {
        triggerError(`A field named '${labelClean}' already exists.`);
        return;
      }
    } else {
      if (fields.some((f, i) => f.label === labelClean && i !== editingFieldIndex)) {
        triggerError(`A field named '${labelClean}' already exists.`);
        return;
      }
    }

    // Build validation rules
    const validation = {};
    let options = null;

    if (newFieldType === 'number') {
      if (numIsFloat) validation.float_decimals = 2;
      if (numMin !== '') validation.min = parseFloat(numMin);
      if (numMax !== '') validation.max = parseFloat(numMax);
      if (numMinLength !== '') validation.min_length = parseInt(numMinLength);
      if (numMaxLength !== '') validation.max_length = parseInt(numMaxLength);
    } else if (newFieldType === 'text') {
      validation.validator_type = textValidator;
      if (textMinLength !== '') validation.min_length = parseInt(textMinLength);
      if (textMaxLength !== '') validation.max_length = parseInt(textMaxLength);
    } else if (newFieldType === 'coordinates') {
      validation.format = coordFormat;
    } else if (newFieldType === 'datetime' || newFieldType === 'date' || newFieldType === 'time') {
      if (dateTimeFormat) validation.date_format = dateTimeFormat.trim();
    } else if (newFieldType === 'options') {
      if (optionSource === 'static') {
        if (optionsList.length === 0) {
          triggerError("Manual options type requires at least one choice configured.");
          return;
        }
        options = optionsList;
      } else {
        options = optionsList.length > 0 ? optionsList : [];
      }
    }

    const rendererRule = customRenderers.find(r => String(r.id) === String(newFieldRender));
    const newField = {
      label: labelClean,
      alias: newFieldAlias.trim() || null,
      type: newFieldType,
      required: newFieldRequired,
      sample_data: newFieldSample.trim() || null,
      options,
      option_source: newFieldType === 'options' ? optionSource : 'static',
      species_field: newFieldType === 'options' && optionSource === 'species_library' ? speciesField : null,
      species_category: newFieldType === 'options' && optionSource === 'species_library' ? speciesCategory : null,
      date_format: (newFieldType === 'datetime' || newFieldType === 'date' || newFieldType === 'time') && dateTimeFormat ? dateTimeFormat.trim() : null,
      validation: Object.keys(validation).length > 0 ? validation : null,
      render: newFieldRender !== 'none' ? newFieldRender : null,
      custom_renderer: (newFieldRender && newFieldRender.startsWith('custom_regex_')) ? rendererRule || null : null,
      description: newFieldDescription.trim() || null
    };

    if (editingFieldIndex === null) {
      setFields([...fields, newField]);
      triggerSuccess("Added field to schema list.");
    } else {
      setFields(fields.map((f, i) => i === editingFieldIndex ? newField : f));
      setEditingFieldIndex(null);
      triggerSuccess("Updated field schema configurations.");
    }
    
    resetFieldForm();
  };

  // Save full data group modifications to backend
  const handleSaveFields = async () => {
    if (!selectedGroup) return;

    const updatedFields = fields.map(f => {
      if (f.render && f.render.startsWith('custom_regex_') && !f.custom_renderer) {
        const rule = customRenderers.find(r => String(r.id) === String(f.render));
        if (rule) {
          return {
            ...f,
            custom_renderer: rule
          };
        }
      }
      return f;
    });

    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups/${selectedGroup.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          fields: updatedFields
        })
      });

      if (res.ok) {
        const updated = await res.json();
        setDataGroups(dataGroups.map(g => g.id === updated.id ? updated : g));
        setSelectedGroup(updated);
        setFields(updated.fields || []);
        triggerSuccess("Data Group schemas saved successfully.");
      } else {
        const err = await res.json();
        triggerError(err.detail || "Failed to save schemas.");
      }
    } catch (err) {
      triggerError("Network error while saving schema modifications.");
    }
  };

  // Delete Data Group
  const handleDeleteGroup = async (groupId) => {
    const confirmed = await modal.confirm("Are you sure you want to delete this entire data group? This action is irreversible.", {
      title: "Delete Data Group",
      confirmText: "Delete Data Group",
      danger: true
    });
    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setDataGroups(dataGroups.filter(g => g.id !== groupId));
        if (selectedGroup?.id === groupId) {
          setSelectedGroup(null);
        }
        triggerSuccess("Data Group deleted successfully.");
      } else {
        triggerError("Failed to delete data group.");
      }
    } catch (err) {
      triggerError("Network error during delete call.");
    }
  };

  // Open Share Overlay
  const handleOpenShares = async (group) => {
    setShareGroupId(group.id);
    setSharedTenantIds([]);
    setShowShareModal(true);

    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups/${group.id}/shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const ids = await res.json();
        setSharedTenantIds(ids);
      }
    } catch (err) {
      console.error("Error loading shared tenants:", err);
    }
  };

  // Toggle Single Tenant Checkbox
  const handleToggleTenantShare = (tenantId) => {
    if (sharedTenantIds.includes(tenantId)) {
      setSharedTenantIds(sharedTenantIds.filter(id => id !== tenantId));
    } else {
      setSharedTenantIds([...sharedTenantIds, tenantId]);
    }
  };

  // Submit Shares
  const handleSaveShares = async () => {
    if (!shareGroupId) return;

    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups/${shareGroupId}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ tenant_ids: sharedTenantIds })
      });

      if (res.ok) {
        setShowShareModal(false);
        triggerSuccess("Dataset sharing rules updated successfully.");
      } else {
        triggerError("Failed to save sharing rules.");
      }
    } catch (err) {
      triggerError("Network error during share registration.");
    }
  };

  const renderRegexModal = () => {
    if (!showRegexModal) return null;

    const testOutput = (() => {
      if (!regexBuilderPattern) return regexBuilderTestInput;
      try {
        let pattern = regexBuilderPattern;
        let flags = 'g';
        if (pattern.startsWith('/')) {
          const lastSlashIdx = pattern.lastIndexOf('/');
          if (lastSlashIdx > 0) {
            flags = pattern.slice(lastSlashIdx + 1) || 'g';
            pattern = pattern.slice(1, lastSlashIdx);
          }
        }
        const re = new RegExp(pattern, flags);
        return regexBuilderTestInput.replace(re, regexBuilderReplace);
      } catch (e) {
        return `Pattern Error: ${e.message}`;
      }
    })();

    const handleSaveRenderer = () => {
      const name = regexBuilderName.trim();
      const pattern = regexBuilderPattern.trim();
      const replace = regexBuilderReplace;

      if (!name) {
        triggerError("Please enter a name for the regex renderer.");
        return;
      }
      if (!pattern) {
        triggerError("Please enter a search regex pattern.");
        return;
      }

      try {
        let testPattern = pattern;
        let flags = 'g';
        if (testPattern.startsWith('/')) {
          const lastSlashIdx = testPattern.lastIndexOf('/');
          if (lastSlashIdx > 0) {
            flags = testPattern.slice(lastSlashIdx + 1) || 'g';
            testPattern = testPattern.slice(1, lastSlashIdx);
          }
        }
        new RegExp(testPattern, flags);
      } catch (e) {
        triggerError(`Invalid regular expression: ${e.message}`);
        return;
      }

      let updated;
      if (editingRendererId) {
        updated = customRenderers.map(r => r.id === editingRendererId ? {
          id: editingRendererId,
          name,
          regex: pattern,
          replace
        } : r);
        triggerSuccess("Updated custom regex renderer.");
      } else {
        const newId = 'custom_regex_' + Date.now();
        updated = [
          ...customRenderers,
          {
            id: newId,
            name,
            regex: pattern,
            replace
          }
        ];
        triggerSuccess("Created new custom regex renderer.");
      }

      setCustomRenderers(updated);
      localStorage.setItem('customRegexRenderers', JSON.stringify(updated));

      setEditingRendererId(null);
      setRegexBuilderName('');
      setRegexBuilderPattern('');
      setRegexBuilderReplace('');
      setRegexBuilderTestInput('');
    };

    const handleDeleteRenderer = async (id, e) => {
      e.stopPropagation();
      const confirmed = await modal.confirm("Are you sure you want to delete this custom regex renderer? Any fields using it will revert to Raw Formatting.", {
        title: "Delete Regex Renderer",
        confirmText: "Delete Renderer",
        danger: true
      });
      if (!confirmed) return;
      const updated = customRenderers.filter(r => r.id !== id);
      setCustomRenderers(updated);
      localStorage.setItem('customRegexRenderers', JSON.stringify(updated));
      if (newFieldRender === id) {
        setNewFieldRender('none');
      }
      triggerSuccess("Deleted custom regex renderer.");
    };

    const handleEditRenderer = (r) => {
      setEditingRendererId(r.id);
      setRegexBuilderName(r.name);
      setRegexBuilderPattern(r.regex);
      setRegexBuilderReplace(r.replace);
    };

    const handleCancelEdit = () => {
      setEditingRendererId(null);
      setRegexBuilderName('');
      setRegexBuilderPattern('');
      setRegexBuilderReplace('');
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '10px',
          padding: '24px',
          width: '780px',
          maxWidth: '95%',
          maxHeight: '90vh',
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          gap: '24px',
          boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
          overflowY: 'auto'
        }}>
          {/* Left Panel: Build/Edit Renderer */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', borderRight: '1px solid var(--border-color, #3c3c42)', paddingRight: '20px' }}>
            <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main, #ffffff)', fontWeight: '700' }}>
              {editingRendererId ? '✏️ Edit Regex Renderer' : '⚙️ Build Regex Renderer'}
            </h4>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: '#8e8e93', fontWeight: '600' }}>Renderer Name</label>
              <input
                type="text"
                placeholder="e.g. Add Dash to Serial Code"
                value={regexBuilderName}
                onChange={(e) => setRegexBuilderName(e.target.value)}
                style={{
                  backgroundColor: '#2b2b2f',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: '#ffffff',
                  fontSize: '12px'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: '#8e8e93', fontWeight: '600' }}>Search Regex Pattern</label>
              <input
                type="text"
                placeholder="e.g. ^([A-Z]{3})(\d{4})$"
                value={regexBuilderPattern}
                onChange={(e) => setRegexBuilderPattern(e.target.value)}
                style={{
                  backgroundColor: '#2b2b2f',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: '#8e8e93', fontWeight: '600' }}>Replacement String</label>
              <input
                type="text"
                placeholder="e.g. $1-$2"
                value={regexBuilderReplace}
                onChange={(e) => setRegexBuilderReplace(e.target.value)}
                style={{
                  backgroundColor: '#2b2b2f',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            {/* Live Testing Sandbox */}
            <div style={{
              border: '1px dashed #3c3c42',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: 'rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginTop: '6px'
            }}>
              <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--primary-color)', textTransform: 'uppercase' }}>Testing Sandbox</span>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '10px', color: '#8e8e93' }}>Test Input Text</label>
                <input
                  type="text"
                  placeholder="Type test value..."
                  value={regexBuilderTestInput}
                  onChange={(e) => setRegexBuilderTestInput(e.target.value)}
                  style={{
                    backgroundColor: '#1b1b1d',
                    border: '1px solid #3c3c42',
                    borderRadius: '4px',
                    padding: '6px 8px',
                    color: '#ffffff',
                    fontSize: '11px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <label style={{ fontSize: '10px', color: '#8e8e93' }}>Result Output</label>
                <div style={{
                  backgroundColor: '#111',
                  borderRadius: '#4px',
                  padding: '8px 10px',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  color: testOutput.startsWith('Pattern Error:') ? '#ff7b72' : '#79c0ff',
                  wordBreak: 'break-all',
                  minHeight: '16px'
                }}>
                  {testOutput || <span style={{ color: '#555' }}>(Empty Result)</span>}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button
                type="button"
                onClick={handleSaveRenderer}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#18181b',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                {editingRendererId ? 'Apply Update' : 'Create Renderer'}
              </button>
              {editingRendererId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#dfdfe3',
                    border: '1px solid #3c3c42',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Right Panel: Renderers List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontSize: '15px', color: '#ffffff', fontWeight: '700' }}>Active Renderers</h4>
              <button
                type="button"
                onClick={() => {
                  setShowRegexModal(false);
                  setEditingRendererId(null);
                  setRegexBuilderName('');
                  setRegexBuilderPattern('');
                  setRegexBuilderReplace('');
                  setRegexBuilderTestInput('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8e8e93',
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
                title="Close Modal"
              >
                ✕
              </button>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              overflowY: 'auto',
              flex: 1,
              maxHeight: '380px',
              paddingRight: '4px'
            }}>
              {/* Default built-in renderers */}
              <div style={{ fontSize: '10px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700', marginTop: '4px' }}>System Built-in</div>
              {['None (none)', 'ALL CAPS (uppercase)', 'all lowercase (lowercase)', 'Capitalize Each Word (titlecase)', 'Metric to Imperial (metric_to_imperial)', 'Zero-Pad to 5 Digits (pad_5)', 'Prepend # & Zero-Pad to 5 Digits (prepend_hash_pad_5)'].map(systemText => (
                <div key={systemText} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#222225',
                  border: '1px solid #2d2d30',
                  borderRadius: '6px',
                  opacity: 0.7
                }}>
                  <span style={{ fontSize: '11px', color: '#dfdfe3' }}>{systemText}</span>
                  <span style={{ fontSize: '10px', color: '#8e8e93', fontStyle: 'italic' }}>read-only</span>
                </div>
              ))}

              {/* Custom renderers */}
              <div style={{ fontSize: '10px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700', marginTop: '10px' }}>Custom Rules</div>
              {customRenderers.length === 0 ? (
                <div style={{ color: '#55555c', fontSize: '11px', fontStyle: 'italic', padding: '8px' }}>
                  No custom regex renderers built yet. Use the builder on the left.
                </div>
              ) : (
                customRenderers.map(r => (
                  <div
                    key={r.id}
                    onClick={() => handleEditRenderer(r)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      backgroundColor: editingRendererId === r.id ? 'rgba(56,139,253,0.1)' : '#222225',
                      border: editingRendererId === r.id ? '1px solid var(--primary-color)' : '1px solid #3c3c42',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}
                      </span>
                      <span style={{ fontSize: '10px', color: '#8e8e93', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        /{r.regex}/ → "{r.replace}"
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteRenderer(r.id, e)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#8e8e93',
                          cursor: 'pointer',
                          padding: '6px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.color = '#ff7b72'}
                        onMouseOut={(e) => e.currentTarget.style.color = '#8e8e93'}
                        title="Delete Custom Renderer"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSyncModal = () => {
    if (!showSyncModal) return null;

    const importedGroupCount = importJsonData
      ? (Array.isArray(importJsonData) ? importJsonData.length : (importJsonData.data_groups?.length || 0))
      : 0;
    const importedRendererCount = importJsonData && importJsonData.custom_renderers
      ? importJsonData.custom_renderers.length
      : 0;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '12px',
          padding: '24px',
          width: '720px',
          maxWidth: '95%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #2d2d30)', paddingBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '22px' }}>🔄</span>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', color: 'var(--text-main, #ffffff)', fontWeight: '700' }}>
                  Synchronize Data Sets
                </h3>
                <span style={{ fontSize: '12px', color: 'var(--text-mute, #8e8e93)' }}>
                  Sync schema definitions, validators, and tenant shares between Debug and Production.
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowSyncModal(false);
                setSyncModalError('');
                setSyncModalSuccess('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-mute, #8e8e93)',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
              title="Close Modal"
            >
              ✕
            </button>
          </div>

          {/* Subtabs: Direct Sync vs JSON File */}
          <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #2d2d30', paddingBottom: '8px' }}>
            <button
              type="button"
              onClick={() => {
                setSyncTab('direct');
                setSyncModalError('');
                setSyncModalSuccess('');
              }}
              style={{
                backgroundColor: syncTab === 'direct' ? '#2563eb' : '#27272a',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🚀 Direct Server Sync
            </button>
            <button
              type="button"
              onClick={() => {
                setSyncTab('file');
                setSyncModalError('');
                setSyncModalSuccess('');
              }}
              style={{
                backgroundColor: syncTab === 'file' ? '#2563eb' : '#27272a',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              📁 JSON File Export / Import
            </button>
          </div>

          {/* Alert Messages */}
          {syncModalError && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              padding: '10px 14px',
              color: '#fca5a5',
              fontSize: '12px'
            }}>
              ⚠️ {syncModalError}
            </div>
          )}
          {syncModalSuccess && (
            <div style={{
              backgroundColor: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid #10b981',
              borderRadius: '6px',
              padding: '10px 14px',
              color: '#6ee7b7',
              fontSize: '12px'
            }}>
              {syncModalSuccess}
            </div>
          )}

          {/* TAB 1: DIRECT SERVER SYNC */}
          {syncTab === 'direct' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                backgroundColor: '#242427',
                border: '1px solid #333338',
                borderRadius: '8px',
                padding: '12px 14px',
                fontSize: '12px',
                color: '#a1a1aa',
                lineHeight: '1.5'
              }}>
                💡 <strong>How it works:</strong> Directly connects to your remote TrophyCoin server via API, authenticates with your Global Admin credentials, and either pushes local schemas or pulls remote schemas.
              </div>

              {/* Direction Selector */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '11px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700' }}>
                  Sync Direction
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div
                    data-testid="sync-direction-push"
                    onClick={() => setSyncDirection('push')}
                    style={{
                      border: syncDirection === 'push' ? '2px solid #3b82f6' : '1px solid #3c3c42',
                      backgroundColor: syncDirection === 'push' ? 'rgba(59, 130, 246, 0.1)' : '#222225',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>📤</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#ffffff' }}>Push to Remote</div>
                      <div style={{ fontSize: '11px', color: '#8e8e93' }}>Upload local datasets to target server</div>
                    </div>
                  </div>
                  <div
                    data-testid="sync-direction-pull"
                    onClick={() => setSyncDirection('pull')}
                    style={{
                      border: syncDirection === 'pull' ? '2px solid #3b82f6' : '1px solid #3c3c42',
                      backgroundColor: syncDirection === 'pull' ? 'rgba(59, 130, 246, 0.1)' : '#222225',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>📥</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#ffffff' }}>Pull from Remote</div>
                      <div style={{ fontSize: '11px', color: '#8e8e93' }}>Download datasets from target server</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Target Server URL */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: '11px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700' }}>
                    Target Server URL
                  </label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => setSyncTargetUrl('https://trophycoin-app-982827923504.us-central1.run.app')}
                      style={{
                        backgroundColor: '#2d2d32',
                        color: '#60a5fa',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '10px',
                        cursor: 'pointer'
                      }}
                    >
                      ☁️ Cloud Run Production
                    </button>
                    <button
                      type="button"
                      onClick={() => setSyncTargetUrl('http://localhost:8000')}
                      style={{
                        backgroundColor: '#2d2d32',
                        color: '#9ca3af',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '10px',
                        cursor: 'pointer'
                      }}
                    >
                      💻 Localhost:8000
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  value={syncTargetUrl}
                  onChange={(e) => setSyncTargetUrl(e.target.value)}
                  placeholder="https://trophycoin-app-982827923504.us-central1.run.app"
                  style={{
                    backgroundColor: '#111',
                    border: '1px solid #3c3c42',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              {/* Target Credentials */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700' }}>
                    Target Admin Email
                  </label>
                  <input
                    type="email"
                    value={syncAdminEmail}
                    onChange={(e) => setSyncAdminEmail(e.target.value)}
                    placeholder="admin@trophycoin.io"
                    style={{
                      backgroundColor: '#111',
                      border: '1px solid #3c3c42',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: '#ffffff',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700' }}>
                    Target Admin Password
                  </label>
                  <input
                    type="password"
                    value={syncAdminPassword}
                    onChange={(e) => setSyncAdminPassword(e.target.value)}
                    placeholder="••••••••••••"
                    style={{
                      backgroundColor: '#111',
                      border: '1px solid #3c3c42',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: '#ffffff',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Mode & Options */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700' }}>
                    Conflict Resolution Mode
                  </label>
                  <select
                    value={syncMode}
                    onChange={(e) => setSyncMode(e.target.value)}
                    style={{
                      backgroundColor: '#111',
                      border: '1px solid #3c3c42',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: '#ffffff',
                      fontSize: '12px'
                    }}
                  >
                    <option value="merge">Merge & Update (Update existing schemas)</option>
                    <option value="add_only">Add Only (Skip existing groups)</option>
                  </select>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: '#d1d5db', marginTop: '18px' }}>
                  <input
                    type="checkbox"
                    checked={syncIncludeCustomRenderers}
                    onChange={(e) => setSyncIncludeCustomRenderers(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Include custom regex renderers
                </label>
              </div>

              {/* Progress message */}
              {syncLoading && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#60a5fa',
                  fontSize: '12px',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  padding: '8px 12px',
                  borderRadius: '6px'
                }}>
                  <span>⏳</span> {syncStatusMsg || 'Syncing in progress...'}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setShowSyncModal(false)}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#9ca3af',
                    border: '1px solid #3c3c42',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  data-testid="execute-remote-sync-btn"
                  type="button"
                  disabled={syncLoading}
                  onClick={handleExecuteRemoteSync}
                  style={{
                    backgroundColor: syncLoading ? '#4b5563' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: syncLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {syncLoading ? 'Syncing...' : (syncDirection === 'push' ? '📤 Push to Remote Server' : '📥 Pull from Remote Server')}
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: JSON FILE EXPORT / IMPORT */}
          {syncTab === 'file' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Export Box */}
              <div style={{
                backgroundColor: '#222225',
                border: '1px solid #333338',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#ffffff', fontWeight: '700' }}>
                    Export All Datasets Bundle
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#8e8e93' }}>
                    Download a portable JSON file containing all {dataGroups.length} data groups, schema fields, and tenant sharing rules.
                  </p>
                </div>
                <button
                  data-testid="modal-export-json-btn"
                  type="button"
                  onClick={handleQuickExportJson}
                  style={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  📥 Export JSON Bundle
                </button>
              </div>

              {/* Import Box */}
              <div style={{
                backgroundColor: '#222225',
                border: '1px solid #333338',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <h4 style={{ margin: 0, fontSize: '14px', color: '#ffffff', fontWeight: '700' }}>
                  Import Datasets from JSON File
                </h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#8e8e93' }}>
                  Select a previously exported <code>trophycoin_datasets_*.json</code> bundle to restore or merge into this server.
                </p>

                <input
                  data-testid="import-json-file-input"
                  type="file"
                  accept=".json"
                  onChange={handleFileImportChange}
                  style={{
                    backgroundColor: '#111',
                    border: '1px solid #3c3c42',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    color: '#ffffff',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                />

                {importJsonData && (
                  <div style={{
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{ fontSize: '12px', color: '#93c5fd', fontWeight: '600' }}>
                      📄 File Ready: {importJsonFileName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#d1d5db', display: 'flex', gap: '16px' }}>
                      <span><strong>{importedGroupCount}</strong> Data Groups</span>
                      <span><strong>{importedRendererCount}</strong> Custom Renderers</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px', marginTop: '6px', alignItems: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: '#8e8e93', textTransform: 'uppercase', fontWeight: '700' }}>
                          Import Mode
                        </label>
                        <select
                          value={syncMode}
                          onChange={(e) => setSyncMode(e.target.value)}
                          style={{
                            backgroundColor: '#111',
                            border: '1px solid #3c3c42',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            color: '#ffffff',
                            fontSize: '12px'
                          }}
                        >
                          <option value="merge">Merge & Update Existing</option>
                          <option value="add_only">Add Only (Skip Existing)</option>
                        </select>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: '#d1d5db', marginTop: '16px' }}>
                        <input
                          type="checkbox"
                          checked={syncIncludeCustomRenderers}
                          onChange={(e) => setSyncIncludeCustomRenderers(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        Import custom regex renderers
                      </label>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                      <button
                        data-testid="execute-file-import-btn"
                        type="button"
                        disabled={syncLoading}
                        onClick={handleExecuteFileImport}
                        style={{
                          backgroundColor: syncLoading ? '#4b5563' : '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 18px',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: syncLoading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {syncLoading ? 'Importing...' : '🚀 Execute File Import'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
      
      {/* Standardized Sub-tab Selectors: Dataset Groups vs Species Library */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        borderBottom: '1px solid var(--border-color)',
        paddingBottom: '0',
        gap: '4px',
        zIndex: 1,
        position: 'relative'
      }}>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setActiveSubTab('datasets')}
            style={{
              backgroundColor: activeSubTab === 'datasets' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
              color: activeSubTab === 'datasets' ? 'var(--primary-color)' : 'var(--text-mute)',
              border: '1px solid transparent',
              borderLeftColor: activeSubTab === 'datasets' ? 'var(--border-color)' : 'transparent',
              borderRightColor: activeSubTab === 'datasets' ? 'var(--border-color)' : 'transparent',
              borderTopColor: activeSubTab === 'datasets' ? 'var(--border-color)' : 'transparent',
              borderBottomColor: activeSubTab === 'datasets' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
              borderRadius: '8px 8px 0 0',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: activeSubTab === 'datasets' ? '600' : '500',
              cursor: 'pointer',
              marginBottom: '-1px',
              transition: 'all 0.15s ease',
              zIndex: activeSubTab === 'datasets' ? 2 : 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: activeSubTab === 'datasets' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'var(--text-main)';
              if (activeSubTab !== 'datasets') {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                e.currentTarget.style.borderRightColor = 'var(--border-color)';
                e.currentTarget.style.borderTopColor = 'var(--border-color)';
              }
            }}
            onMouseOut={(e) => {
              if (activeSubTab !== 'datasets') {
                e.currentTarget.style.color = 'var(--text-mute)';
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderLeftColor = 'transparent';
                e.currentTarget.style.borderRightColor = 'transparent';
                e.currentTarget.style.borderTopColor = 'transparent';
              } else {
                e.currentTarget.style.color = 'var(--primary-color)';
              }
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
            </svg>
            Dataset Groups
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('species')}
            style={{
              backgroundColor: activeSubTab === 'species' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
              color: activeSubTab === 'species' ? 'var(--primary-color)' : 'var(--text-mute)',
              border: '1px solid transparent',
              borderLeftColor: activeSubTab === 'species' ? 'var(--border-color)' : 'transparent',
              borderRightColor: activeSubTab === 'species' ? 'var(--border-color)' : 'transparent',
              borderTopColor: activeSubTab === 'species' ? 'var(--border-color)' : 'transparent',
              borderBottomColor: activeSubTab === 'species' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
              borderRadius: '8px 8px 0 0',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: activeSubTab === 'species' ? '600' : '500',
              cursor: 'pointer',
              marginBottom: '-1px',
              transition: 'all 0.15s ease',
              zIndex: activeSubTab === 'species' ? 2 : 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: activeSubTab === 'species' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'var(--text-main)';
              if (activeSubTab !== 'species') {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                e.currentTarget.style.borderRightColor = 'var(--border-color)';
                e.currentTarget.style.borderTopColor = 'var(--border-color)';
              }
            }}
            onMouseOut={(e) => {
              if (activeSubTab !== 'species') {
                e.currentTarget.style.color = 'var(--text-mute)';
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderLeftColor = 'transparent';
                e.currentTarget.style.borderRightColor = 'transparent';
                e.currentTarget.style.borderTopColor = 'transparent';
              } else {
                e.currentTarget.style.color = 'var(--primary-color)';
              }
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
            Species Library
          </button>
        </div>
      </div>

      <div style={{
        background: 'var(--panel-color, var(--card-bg))',
        border: '1px solid var(--border-color)',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        position: 'relative',
        zIndex: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        {activeSubTab === 'species' ? (
        <SpeciesLibrary user={user} />
      ) : (
        <>
          {/* Alert Banners */}
      {successMsg && (
        <div style={{
          backgroundColor: 'rgba(46, 160, 67, 0.15)',
          border: '1px solid rgba(46, 160, 67, 0.4)',
          borderRadius: '8px',
          color: '#3fb950',
          padding: '12px 18px',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          ✅ {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{
          backgroundColor: 'rgba(248, 81, 73, 0.15)',
          border: '1px solid rgba(248, 81, 73, 0.4)',
          borderRadius: '8px',
          color: '#ff7b72',
          padding: '12px 18px',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          ❌ {errorMsg}
        </div>
      )}

      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-main)', fontWeight: '700' }}>Global Dataset Manager</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
            Define structured data group schemas, add custom validators, and share datasets with multiple tenants.
          </p>
        </div>
        {!selectedGroup && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isGlobalAdmin && (
              <>
                <button
                  data-testid="sync-datasets-btn"
                  onClick={() => {
                    setSyncModalError('');
                    setSyncModalSuccess('');
                    setShowSyncModal(true);
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#60a5fa',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title="Direct Remote Sync or JSON Import/Export with Production"
                >
                  🔄 Sync to Production
                </button>
                <button
                  data-testid="export-datasets-btn"
                  onClick={handleQuickExportJson}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#10b981',
                    border: '1px solid #10b981',
                    borderRadius: '6px',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title="Export all data sets as JSON bundle"
                >
                  📤 Export JSON
                </button>
              </>
            )}
            <button
              onClick={() => setShowAddModal(true)}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ➕ Add Data Group
            </button>
          </div>
        )}
      </div>

      {/* 1. LIST VIEW */}
      {!selectedGroup && (
        <div style={{
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          {dataGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-mute)' }}>
              <span style={{ fontSize: '36px' }}>🗄️</span>
              <p style={{ margin: '10px 0 0 0', fontSize: '14px', fontWeight: '600' }}>No Data Groups Created Yet</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>Click "Add Data Group" at the top right to start.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.1)' }}>
                  <th style={{ padding: '12px 16px', fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase' }}>Data Group Name</th>
                  <th style={{ padding: '12px 16px', fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase' }}>Description</th>
                  <th style={{ padding: '12px 16px', fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', textAlign: 'center' }}>Fields Count</th>
                  <th style={{ padding: '12px 16px', fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dataGroups.map(group => (
                  <tr key={group.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>{group.name}</td>
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: 'var(--text-mute)' }}>{group.description || '--'}</td>
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: 'var(--text-main)', textAlign: 'center' }}>{(group.fields || []).length}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '8px' }}>
                        <button
                          onClick={() => handleOpenEditor(group)}
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            color: 'var(--text-main)',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ⚙️ Schema Editor
                        </button>
                        <button
                          onClick={() => handleOpenEditDetails(group)}
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            color: 'var(--text-main)',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Edit Details
                        </button>
                        <button
                          onClick={() => handleDuplicateGroup(group)}
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            color: 'var(--text-main)',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          👥 Duplicate
                        </button>
                        <button
                          onClick={() => handleOpenShares(group)}
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            color: 'var(--primary-color)',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          🔗 Share Tenant
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group.id)}
                          style={{
                            backgroundColor: 'rgba(248, 81, 73, 0.1)',
                            border: '1px solid rgba(248, 81, 73, 0.2)',
                            borderRadius: '4px',
                            color: '#ff7b72',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 2. SCHEMA EDITOR VIEW */}
      {selectedGroup && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Editor Header Card */}
          <div style={{
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--primary-color)', textTransform: 'uppercase' }}>Editing Dataset Schema</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', fontWeight: '700' }}>{selectedGroup.name}</h4>
                <button
                  onClick={() => handleOpenEditDetails(selectedGroup)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-color)',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '2px 4px',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                  title="Edit Dataset Name / Description"
                >
                  ✏️
                </button>
              </div>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>{selectedGroup.description}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSaveFields}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                💾 Save Changes
              </button>
              <button
                onClick={() => setSelectedGroup(null)}
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  color: 'var(--text-main)',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ⬅️ Back to List
              </button>
            </div>
          </div>

          {/* Main Editor Columns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '20px',
            alignItems: 'start'
          }}>
            
            {/* Left Column: Current Fields Schema */}
            <div style={{
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '10px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <h5 style={{ margin: 0, fontSize: '13px', color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Field Hierarchy Order</h5>
              
              {fields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', border: '1px dashed var(--border-color)', borderRadius: '8px', color: 'var(--text-mute)' }}>
                  No fields defined. Build your data fields using the form on the right.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {fields.map((field, idx) => (
                    <div
                      key={`field-row-${idx}`}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={(e) => handleDrop(e, idx)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditField(field, idx)}
                      onMouseOver={(e) => {
                        if (editingFieldIndex !== idx) {
                          e.currentTarget.style.backgroundColor = 'var(--panel-color)';
                          e.currentTarget.style.borderColor = 'var(--text-mute)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (editingFieldIndex !== idx) {
                          e.currentTarget.style.backgroundColor = 'var(--bg-color)';
                          e.currentTarget.style.borderColor = 'var(--border-color)';
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        backgroundColor: editingFieldIndex === idx ? 'rgba(56, 139, 253, 0.1)' : 'var(--bg-color)',
                        border: editingFieldIndex === idx ? '1px solid var(--primary-color)' : '1px solid var(--border-color)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        opacity: draggedFieldIndex === idx ? 0.4 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                        <div 
                          style={{ 
                            color: 'var(--text-mute)', 
                            cursor: 'grab', 
                            fontSize: '14px', 
                            userSelect: 'none',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Drag to reorder"
                        >
                          ⋮⋮
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                              {field.alias ? `${field.alias} (${field.label})` : field.label}
                            </span>
                            {field.required && (
                              <span style={{ fontSize: '9px', backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>REQUIRED</span>
                            )}
                          </div>
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase' }}>
                            Type: <strong>{field.type}</strong>
                            {field.type === 'number' && field.validation?.float_decimals && ' (Float)'}
                            {field.type === 'coordinates' && ` (${field.validation?.format?.toUpperCase()})`}
                            {field.type === 'options' && (
                              field.option_source === 'species_library' ? (
                                <span style={{ 
                                  color: '#38bdf8', 
                                  backgroundColor: 'rgba(56, 189, 248, 0.15)', 
                                  border: '1px solid rgba(56, 189, 248, 0.3)',
                                  borderRadius: '4px',
                                  padding: '1px 6px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  marginLeft: '6px',
                                  textTransform: 'none'
                                }}>
                                  🌿 Species Library [{field.species_field || 'common_name'}{field.species_category && field.species_category !== 'All' ? ` | Cat: ${field.species_category}` : ''}]
                                </span>
                              ) : (
                                ` (${(field.options || []).length} choices)`
                              )
                            )}
                            {(field.type === 'datetime' || field.type === 'date' || field.type === 'time') && (field.date_format || field.validation?.date_format) && (
                              <span style={{ color: 'var(--primary-color)', textTransform: 'none', marginLeft: '6px' }}>
                                Format: <strong>{field.date_format || field.validation?.date_format}</strong>
                              </span>
                            )}
                          </span>
                          {field.sample_data && (
                            <span style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                              Sample: <span style={{ fontFamily: 'monospace', color: 'var(--primary-color)', fontWeight: '600' }}>"{field.sample_data}"</span>
                              {field.render && field.render !== 'none' && (
                                <>
                                  {' → '}
                                  <span style={{ fontFamily: 'monospace', color: '#2da44e', fontWeight: '600' }}>
                                    "{getFormattedSampleData(field.sample_data, field.render)}"
                                  </span>
                                </>
                              )}
                              {(field.type === 'datetime' || field.type === 'date' || field.type === 'time') && (field.date_format || field.validation?.date_format) && (
                                <>
                                  {' → '}
                                  <span style={{ fontFamily: 'monospace', color: '#eab308', fontWeight: '600' }}>
                                    "{formatDateTimeValue(field.sample_data, field.date_format || field.validation?.date_format)}"
                                  </span>
                                </>
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {/* Delete */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Avoid triggering row selection click
                            deleteField(idx);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-mute)',
                            cursor: 'pointer',
                            padding: '6px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.color = '#ff7b72'}
                          onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-mute)'}
                          title="Delete Field"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Build Field Creator / Editor */}
            <div style={{
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '10px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 style={{ margin: 0, fontSize: '13px', color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {editingFieldIndex !== null ? '✏️ Edit Data Field' : 'Add New Data Field'}
                </h5>
                {editingFieldIndex !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingFieldIndex(null);
                      resetFieldForm();
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ff7b72',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
              
              <form onSubmit={handleAddField} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* Field Label / System Key */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Field System ID / Key (e.g. catch_weight)</label>
                  <input
                    type="text"
                    required
                    placeholder="letters, numbers and underscores only"
                    value={newFieldLabel}
                    onChange={(e) => setNewFieldLabel(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text)',
                      fontSize: '12px'
                    }}
                  />
                </div>

                {/* Field Alias / Form Label */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Field Alias / Form Label (e.g. Catch Weight)</label>
                  <input
                    type="text"
                    placeholder="User-friendly name shown on entry forms"
                    value={newFieldAlias}
                    onChange={(e) => setNewFieldAlias(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text)',
                      fontSize: '12px'
                    }}
                  />
                </div>

                {/* Field Type Selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Field Type</label>
                  <select
                    value={newFieldType}
                    onChange={(e) => setNewFieldType(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="text">Text (Email, URLs, strings)</option>
                    <option value="number">Number (Floats, integers, ranges)</option>
                    <option value="mobile">Mobile Number (International phone)</option>
                    <option value="datetime">Datetime / Date / Time</option>
                    <option value="coordinates">Geographic Coordinates (DD, DMS, UTM)</option>
                    <option value="options">Predefined Options (Select dropdown)</option>
                  </select>
                </div>

                {/* Sample Data */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Sample Data / Example Value (e.g. Freddie Kruger)</label>
                  <input
                    type="text"
                    placeholder="Enter an example value for this field type"
                    value={newFieldSample}
                    onChange={(e) => setNewFieldSample(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text)',
                      fontSize: '12px'
                    }}
                  />
                </div>

                {/* Required Flag */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                  <input
                    type="checkbox"
                    id="req_box"
                    checked={newFieldRequired}
                    onChange={(e) => setNewFieldRequired(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label htmlFor="req_box" style={{ fontSize: '12px', color: 'var(--text-main)', cursor: 'pointer', fontWeight: '600' }}>
                    Make this field required in operator forms
                  </label>
                </div>

                {/* Render formatting option */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Value Auto-Formatting (Render Option)</label>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <select
                      value={newFieldRender}
                      onChange={(e) => setNewFieldRender(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: 'var(--input-text)',
                        fontSize: '12px',
                        cursor: 'pointer',
                        flex: 1
                      }}
                    >
                      <option value="none">None (Raw Input Value)</option>
                      <option value="uppercase">ALL CAPS (e.g. FREDDIE)</option>
                      <option value="lowercase">all lowercase (e.g. freddie)</option>
                      <option value="titlecase">Capitalize Each Word (e.g. Freddie Kruger)</option>
                      <option value="metric_to_imperial">Metric to Imperial Conversions (e.g. kg to lbs)</option>
                      <option value="pad_5">Zero-Pad to 5 Digits (e.g. 00042)</option>
                      <option value="prepend_hash_pad_5">Prepend # & Zero-Pad to 5 Digits (e.g. #00042)</option>
                      {customRenderers.map(r => (
                        <option key={`render-opt-${r.id}`} value={r.id}>
                          {r.name} (Custom Regex)
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowRegexModal(true)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--text-main)',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-color)'}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--input-border)'}
                      title="Manage Regex Renderers"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-main)' }}>
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Description helper text for form completion */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Field Help / Description</label>
                  <textarea
                    placeholder="Enter instructions or guidance for users when filling in this field..."
                    value={newFieldDescription}
                    onChange={(e) => setNewFieldDescription(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: 'var(--input-text)',
                      fontSize: '12px',
                      minHeight: '60px',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* CONDITIONAL SUB-CONFIG: NUMBER TYPE */}
                {newFieldType === 'number' && (
                  <div style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)' }}>NUMBER RESTRICTIONS</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="float_box"
                        checked={numIsFloat}
                        onChange={(e) => setNumIsFloat(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="float_box" style={{ fontSize: '11px', color: 'var(--text-main)', cursor: 'pointer' }}>Float value with 2 decimals (e.g. 14.82)</label>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Min Value</label>
                        <input
                          type="number"
                          placeholder="none"
                          value={numMin}
                          onChange={(e) => setNumMin(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '4px 6px', color: 'var(--input-text)', fontSize: '11px' }}
                        />
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Max Value</label>
                        <input
                          type="number"
                          placeholder="none"
                          value={numMax}
                          onChange={(e) => setNumMax(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '4px 6px', color: 'var(--input-text)', fontSize: '11px' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Min Length (Digits)</label>
                        <input
                          type="number"
                          placeholder="none"
                          value={numMinLength}
                          onChange={(e) => setNumMinLength(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '4px 6px', color: 'var(--input-text)', fontSize: '11px' }}
                        />
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Max Length (Digits)</label>
                        <input
                          type="number"
                          placeholder="none"
                          value={numMaxLength}
                          onChange={(e) => setNumMaxLength(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '4px 6px', color: 'var(--input-text)', fontSize: '11px' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* CONDITIONAL SUB-CONFIG: TEXT TYPE */}
                {newFieldType === 'text' && (
                  <div style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)' }}>TEXT VALIDATORS</span>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '10px', color: 'var(--text-mute)' }}>Validator Type</label>
                      <select
                        value={textValidator}
                        onChange={(e) => setTextValidator(e.target.value)}
                        style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '6px', color: 'var(--input-text)', fontSize: '11px', cursor: 'pointer' }}
                      >
                        <option value="none">No verification (Any text)</option>
                        <option value="email">Email format validation</option>
                        <option value="website">Website URL validation</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Min Length</label>
                        <input
                          type="number"
                          placeholder="none"
                          value={textMinLength}
                          onChange={(e) => setTextMinLength(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '4px 6px', color: 'var(--input-text)', fontSize: '11px' }}
                        />
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Max Length</label>
                        <input
                          type="number"
                          placeholder="none"
                          value={textMaxLength}
                          onChange={(e) => setTextMaxLength(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '4px 6px', color: 'var(--input-text)', fontSize: '11px' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* CONDITIONAL SUB-CONFIG: COORDINATES */}
                {newFieldType === 'coordinates' && (
                  <div style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)', marginBottom: '4px' }}>COORDINATES FORMAT</span>
                    <label style={{ fontSize: '10px', color: 'var(--text-mute)' }}>Display format configuration</label>
                    <select
                      value={coordFormat}
                      onChange={(e) => setCoordFormat(e.target.value)}
                      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '6px', color: 'var(--input-text)', fontSize: '11px', cursor: 'pointer' }}
                    >
                      <option value="utm">Metric Integer format (Easting/Northing or UTM) - DEFAULT</option>
                      <option value="dd">Decimal Degrees (DD) (e.g. -15.421, 28.291)</option>
                      <option value="dms">Degrees Minutes Seconds (DMS)</option>
                    </select>
                  </div>
                )}

                {/* CONDITIONAL SUB-CONFIG: DATETIME FORMAT */}
                {(newFieldType === 'datetime' || newFieldType === 'date' || newFieldType === 'time') && (
                  <div style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)' }}>
                      DATE & TIME FORMAT
                    </span>
                    <label style={{ fontSize: '10px', color: 'var(--text-mute)' }}>
                      Specify the format string passed to forms and card templates (e.g. YYYY/MM/DD or H:i)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={DATE_TIME_FORMAT_PRESETS.some(p => p.value === dateTimeFormat && p.value !== 'custom' && p.value !== '') ? dateTimeFormat : (dateTimeFormat ? 'custom' : '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === 'custom') {
                            setDateTimeFormat(prev => prev || 'YYYY/MM/DD');
                          } else {
                            setDateTimeFormat(val);
                          }
                        }}
                        style={{ flex: 1, backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '4px', padding: '6px', color: 'var(--input-text)', fontSize: '11px', cursor: 'pointer' }}
                      >
                        {DATE_TIME_FORMAT_PRESETS.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                      {(dateTimeFormat && (!DATE_TIME_FORMAT_PRESETS.some(p => p.value === dateTimeFormat && p.value !== 'custom' && p.value !== '') || dateTimeFormat === 'custom')) && (
                        <input
                          type="text"
                          placeholder="e.g. YYYY/MM/DD or H:i"
                          value={dateTimeFormat === 'custom' ? '' : dateTimeFormat}
                          onChange={(e) => setDateTimeFormat(e.target.value)}
                          style={{ width: '130px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--primary-color)', borderRadius: '4px', padding: '6px', color: 'var(--input-text)', fontSize: '11px' }}
                          title="Custom tokens: YYYY, YY, MMMM, MMM, MM, DD, D, HH, H, hh, h, mm, i, ss, A, a"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* CONDITIONAL SUB-CONFIG: OPTIONS (SELECT) */}
                {newFieldType === 'options' && (
                  <div style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)' }}>
                        CHOICE LIST SOURCE
                      </span>
                      <div style={{ display: 'inline-flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                        <button
                          type="button"
                          onClick={() => setOptionSource('static')}
                          style={{
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            border: 'none',
                            backgroundColor: optionSource === 'static' ? 'var(--primary-color)' : 'var(--bg-color)',
                            color: optionSource === 'static' ? '#000' : 'var(--text-mute)',
                            cursor: 'pointer'
                          }}
                        >
                          Manual Options
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOptionSource('species_library');
                            fetchSpeciesPreview(speciesCategory);
                          }}
                          style={{
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            border: 'none',
                            backgroundColor: optionSource === 'species_library' ? '#38bdf8' : 'var(--bg-color)',
                            color: optionSource === 'species_library' ? '#000' : 'var(--text-mute)',
                            cursor: 'pointer'
                          }}
                        >
                          🌿 Species Library
                        </button>
                      </div>
                    </div>

                    {optionSource === 'static' ? (
                      <>
                        <label style={{ fontSize: '10px', color: 'var(--text-mute)' }}>
                          Set up manual dropdown options for the select input field.
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowOptionsModal(true)}
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--primary-color)',
                            color: 'var(--primary-color)',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          ⚙️ Manage Options ({optionsList.length} set)
                        </button>
                        {optionsList.length > 0 && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: 'var(--text-main)', 
                            marginTop: '4px', 
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            padding: '6px 10px',
                            backgroundColor: 'var(--bg-color)',
                            maxHeight: '120px',
                            overflowY: 'auto'
                          }}>
                            <div style={{ fontWeight: '700', marginBottom: '4px', color: 'var(--text-mute)', fontSize: '10px', textTransform: 'uppercase' }}>Active Options list:</div>
                            {optionsList.map((opt, oidx) => (
                              <div key={`opt-view-${oidx}`} style={{ padding: '2px 0', borderBottom: oidx === optionsList.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.03)' }}>
                                • {opt}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{
                          backgroundColor: 'rgba(56, 189, 248, 0.08)',
                          border: '1px solid rgba(56, 189, 248, 0.25)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          fontSize: '11px',
                          color: '#e2e8f0',
                          lineHeight: '1.4'
                        }}>
                          <div style={{ fontWeight: '700', color: '#38bdf8', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span>🌿 Dynamic Species Library</span>
                          </div>
                          <div>
                            Draws live dropdown choices from the <strong>Species Library & Biological Registry</strong>. 
                            When an operator logs a card, the options automatically respect their tenant's allowed categories (e.g. <em>Fisherman's Tale</em> operators only see Fish).
                          </div>
                        </div>

                        {/* Species Source Field Picker */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>
                            Species Attribute to Display / Value:
                          </label>
                          <select
                            value={speciesField}
                            onChange={(e) => setSpeciesField(e.target.value)}
                            style={{
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px',
                              padding: '6px 8px',
                              color: 'var(--input-text)',
                              fontSize: '11px'
                            }}
                          >
                            <option value="common_name">Common Name (e.g. "Tiger Fish")</option>
                            <option value="scientific_name">Scientific Name (e.g. "Hydrocynus vittatus")</option>
                            <option value="common_and_scientific">Common Name & Scientific Name (e.g. "Tiger Fish (Hydrocynus vittatus)")</option>
                            <option value="subcategory">Subcategory (e.g. "Freshwater Gamefish")</option>
                            <option value="category">Category (e.g. "Fish")</option>
                          </select>
                        </div>

                        {/* Category Filter Picker */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>
                            Category Scope:
                          </label>
                          <select
                            value={speciesCategory}
                            onChange={(e) => {
                              const newCat = e.target.value;
                              setSpeciesCategory(newCat);
                              fetchSpeciesPreview(newCat);
                            }}
                            style={{
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px',
                              padding: '6px 8px',
                              color: 'var(--input-text)',
                              fontSize: '11px'
                            }}
                          >
                            <option value="All">All Categories (Tenant Filter Governs)</option>
                            <option value="Fish">🐟 Fish</option>
                            <option value="Mammals">🦌 Mammals</option>
                            <option value="Birds">🦅 Birds</option>
                            <option value="Reptiles">🦎 Reptiles</option>
                            <option value="Amphibians">🐸 Amphibians</option>
                            <option value="Invertebrates">🦀 Invertebrates</option>
                            <option value="Other">🐾 Other</option>
                          </select>
                        </div>

                        {/* Live Preview of Current Species Library Options */}
                        <div style={{
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          backgroundColor: 'var(--bg-color)',
                          maxHeight: '120px',
                          overflowY: 'auto'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontWeight: '700', color: 'var(--text-mute)', fontSize: '10px', textTransform: 'uppercase' }}>
                              Available Library Options ({speciesLibraryPreview.length}):
                            </span>
                            <button
                              type="button"
                              onClick={() => fetchSpeciesPreview(speciesCategory)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--primary-color)',
                                fontSize: '10px',
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                          {speciesLibraryPreview.length === 0 ? (
                            <div style={{ color: 'var(--text-mute)', fontStyle: 'italic', fontSize: '11px' }}>
                              No species found in registry for this category scope.
                            </div>
                          ) : (
                            speciesLibraryPreview.slice(0, 15).map((sp, sIdx) => {
                              let displayVal = sp.common_name;
                              if (speciesField === 'scientific_name') displayVal = sp.scientific_name || sp.common_name;
                              else if (speciesField === 'common_and_scientific') displayVal = sp.scientific_name ? `${sp.common_name} (${sp.scientific_name})` : sp.common_name;
                              else if (speciesField === 'category') displayVal = sp.category;
                              else if (speciesField === 'subcategory') displayVal = sp.subcategory || sp.category;

                              return (
                                <div key={`sp-prev-${sp.id || sIdx}`} style={{ padding: '2px 0', fontSize: '11px', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                  • {displayVal} <span style={{ opacity: 0.5, fontSize: '10px' }}>[{sp.category}]</span>
                                </div>
                              );
                            })
                          )}
                          {speciesLibraryPreview.length > 15 && (
                            <div style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic', marginTop: '2px' }}>
                              + {speciesLibraryPreview.length - 15} more in registry...
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--primary-color)',
                    color: 'var(--primary-color)',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '4px',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                    e.currentTarget.style.color = 'var(--primary-color)';
                  }}
                >
                  {editingFieldIndex !== null ? '💾 Update Field in Schema' : '➕ Add Field to Schema'}
                </button>

              </form>
            </div>

          </div>

        </div>
      )}

      {/* 3. MODAL DIALOGS */}
      
      {/* Add Group Modal Overlay */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '24px',
            width: '450px',
            maxWidth: '90%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
          }}>
            <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)', fontWeight: '700' }}>
              {editingGroup ? "Edit Data Group Details" : "Add Data Group Schema"}
            </h4>
            
            <form onSubmit={editingGroup ? handleUpdateGroupDetails : handleCreateGroup} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Data Group Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Angler Catch Record"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    color: 'var(--input-text)',
                    fontSize: '12px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Description</label>
                <textarea
                  placeholder="Enter details about this dataset schema..."
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    color: 'var(--input-text)',
                    fontSize: '12px',
                    height: '80px',
                    resize: 'none'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="submit"
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingGroup ? "Save Details" : "Create Schema"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingGroup(null);
                    setNewGroupName('');
                    setNewGroupDesc('');
                  }}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    color: 'var(--text-main)',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Group Modal Overlay */}
      {showShareModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
          }}>
            <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)', fontWeight: '700' }}>Share Dataset Schema</h4>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)' }}>
              Select tenants authorized to bind this data group in their Template Designer and operator brag cards.
            </p>

            <div style={{
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-color)',
              maxHeight: '200px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {tenants.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-mute)', fontSize: '12px' }}>
                  No tenants registered on the platform.
                </div>
              ) : (
                tenants.map(tenant => {
                  const isChecked = sharedTenantIds.includes(tenant.id);
                  return (
                    <div
                      key={`share-tenant-${tenant.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 14px',
                        borderBottom: '1px solid var(--border-color)',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleToggleTenantShare(tenant.id)}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        readOnly
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: 'var(--text-main)', fontWeight: '500' }}>
                        {tenant.brand_name} <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>({tenant.domain_name})</span>
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
              <button
                type="button"
                onClick={handleSaveShares}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save Shares
              </button>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  color: 'var(--text-main)',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Options List Config Modal Overlay */}
      {showOptionsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '24px',
            width: '450px',
            maxWidth: '90%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
          }}>
            <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)', fontWeight: '700' }}>Configure Selection Options</h4>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)' }}>
              Add and remove choice values that operators can select from a dropdown list.
            </p>

            {/* Add Option Form */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder="Enter choice name (e.g. Baitcasting Reel)"
                value={newOptionInput}
                onChange={(e) => setNewOptionInput(e.target.value)}
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: 'var(--input-text)',
                  fontSize: '12px',
                  flex: 1
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (newOptionInput.trim()) {
                    if (optionsList.includes(newOptionInput.trim())) {
                      triggerError("This choice already exists in the list.");
                    } else {
                      setOptionsList([...optionsList, newOptionInput.trim()]);
                      setNewOptionInput('');
                    }
                  }
                }}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Add
              </button>
            </div>

            {/* Options List */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              padding: '8px'
            }}>
              {optionsList.length === 0 ? (
                <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontStyle: 'italic', padding: '10px 0', textAlign: 'center' }}>
                  No options configured yet.
                </div>
              ) : (
                optionsList.map((opt, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '4px'
                  }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-main)' }}>{opt}</span>
                    <button
                      type="button"
                      onClick={() => setOptionsList(optionsList.filter((_, i) => i !== idx))}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-mute)',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.color = '#ff7b72'}
                      onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-mute)'}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => setShowOptionsModal(false)}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close & Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {renderRegexModal()}
      {renderSyncModal()}
        </>
      )}
      </div>
    </div>
  );
}
